// Version: 1.8
// v1.8: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.7: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// 1.6: Acknowledgement support — accepts acks, isAdmin, onAck, onUnack, onSummary
//      props from CyberSecurityHub. Renders InlineAck on each failed check row.
//      Calls onSummary when loading completes so the analysis strip updates.
//      Tile stat labels updated: "Protections active" and "Needs attention".
//      Domain header badge lowercased to "all checks passed".
// 1.5: Tile stat labels changed from "Checks Passed/Checks Failed" to
//      "Protections active / Needs attention".
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authFetch, useAuth } from './auth';

// ── Shared helpers ────────────────────────────────────────────────────────────

function CheckPill({ label, valid, issue }) {
  const color = valid ? '#30d158' : '#ff2d55';
  return (
    <span
      title={!valid && issue ? issue : label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '2px 7px', borderRadius: 4,
        fontSize: 10, fontWeight: 700,
        background: `${color}14`, border: `1px solid ${color}40`,
        color, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
        cursor: !valid && issue ? 'help' : 'default',
      }}
    >
      <span style={{ fontSize: 9 }}>{valid ? '✓' : '✗'}</span>
      {label}
    </span>
  );
}

const CHECKS = ['spf', 'dmarc', 'dkim', 'caa', 'dnssec'];
const CHECK_LABELS = { spf: 'SPF', dmarc: 'DMARC', dkim: 'DKIM', caa: 'CAA', dnssec: 'DNSSEC' };

function checkValid(domain, key) {
  if (!domain) return false;
  if (key === 'dnssec') return domain.dnssec?.enabled === true;
  return domain[key]?.valid === true;
}

function checkIssue(domain, key) {
  if (!domain) return '';
  if (key === 'dnssec') return domain.dnssec?.issue || '';
  return domain[key]?.issue || '';
}

function domainIssueCount(domain) {
  return CHECKS.filter(k => !checkValid(domain, k)).length;
}

function overallColor(n) {
  if (n === 0) return '#30d158';
  if (n <= 2)  return '#e6b800';
  return '#ff2d55';
}

// ── Inline acknowledgement UI (used by the detail view only) ──────────────────

const ACK_STATUSES = [
  "Monitoring",
  "Remediation in progress",
  "Raised with third party",
  "Accepted risk",
  "False positive",
];

const ACK_STATUS_COLORS = {
  "Monitoring":               "#86868b",
  "Remediation in progress":  "#00d4ff",
  "Raised with third party":  "#ff9f0a",
  "Accepted risk":            "#e6b800",
  "False positive":           "#30d158",
};

function InlineAck({ checkKey, acks, isAdmin, onAck, onUnack }) {
  const ack     = acks?.[checkKey];
  const [open, setOpen]           = useState(false);
  const [status, setStatus]       = useState(ACK_STATUSES[0]);
  const [ticketRef, setTicketRef] = useState('');
  const [saving, setSaving]       = useState(false);

  if (ack) {
    const color = ACK_STATUS_COLORS[ack.status] || '#86868b';
    const date  = ack.set_at
      ? new Date(ack.set_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8, padding: '6px 10px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
          STG: {ack.status}
        </span>
        {date && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{date}</span>}
        {ack.ticket_ref && (
          <a href="/tickets" style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', marginLeft: 'auto' }}>
            {ack.ticket_ref} →
          </a>
        )}
        {isAdmin && (
          <button className="btn btn-quiet btn-sm" style={{ marginLeft: ack.ticket_ref ? 0 : 'auto' }}
            onClick={() => onUnack?.(checkKey)}
            title="Remove acknowledgement"
           
          >
            Remove ×
          </button>
        )}
      </div>
    );
  }

  if (!isAdmin) return null;

  const sel = { cursor: 'pointer', flex: 1, minWidth: 0 };

  const handleSave = async () => {
    setSaving(true);
    await onAck?.(checkKey, status, ticketRef || null);
    setSaving(false);
    setOpen(false);
    setTicketRef('');
  };

  if (!open) return (
    <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}
      onClick={() => setOpen(true)}
     
    >
      Acknowledge
    </button>
  );

  return (
    <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select className="input-sm" value={status} onChange={e => setStatus(e.target.value)} style={sel}>
          {ACK_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <input className="input-sm"
          value={ticketRef}
          onChange={e => setTicketRef(e.target.value)}
          placeholder="Ticket ref (optional, e.g. TKT-0042)"
          style={{ ...sel, minWidth: 180 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Dashboard tile (exported named) ──────────────────────────────────────────

export function EmailSecurityTile({ slug }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    authFetch(`/api/clients/${slug}/dns`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  const dns     = data?.data;
  const results = dns?.results || {};
  const domains = Object.values(results);
  const total   = dns?.total_issues ?? domains.reduce((s, d) => s + domainIssueCount(d), 0);
  const color   = overallColor(total);
  const scanned = dns?.scanned_at
    ? new Date(dns.scanned_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'
    : null;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderTop: `3px solid ${loading || !data?.available ? 'var(--border)' : color}`,
      borderRadius: 'var(--radius-lg)',
      padding: '16px 18px',
      display: 'flex', flexDirection: 'column', gap: 10,
      minHeight: 160,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          Email and Domain Security
        </div>
        <a href={`/clients/${slug}/cyber-security`} style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
          View →
        </a>
      </div>
      <div style={{ flex: 1 }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80 }}>
            <span className="spinner" />
          </div>
        ) : !data?.available ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No scan data yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 8,
                background: `${color}22`, border: `1px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 17, fontWeight: 900, color, flexShrink: 0,
              }}>
                {total === 0 ? '✓' : total}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color }}>
                  {total === 0 ? 'No issues found' : `${total} issue${total !== 1 ? 's' : ''} detected`}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  {domains.length} domain{domains.length !== 1 ? 's' : ''} checked
                </div>
              </div>
            </div>
            {domains.map(d => (
              <div key={d.domain} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {d.domain}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {CHECKS.map(k => (
                    <CheckPill key={k} label={CHECK_LABELS[k]} valid={checkValid(d, k)} issue={checkIssue(d, k)} />
                  ))}
                </div>
              </div>
            ))}
            {scanned && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Updated {scanned}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Full detail view (default export, used in Cyber Security Hub) ─────────────

const CHECK_DESCRIPTIONS = {
  spf:    { name: 'SPF',    full: 'Sender Policy Framework',     desc: 'Defines which mail servers are authorised to send email on behalf of your domain. Without this, anyone can send email pretending to be you.' },
  dmarc:  { name: 'DMARC',  full: 'DMARC Policy',                desc: "Controls what happens to emails that fail SPF or DKIM checks. A policy of 'none' means no action is taken — it should be set to quarantine or reject." },
  dkim:   { name: 'DKIM',   full: 'DomainKeys Identified Mail',  desc: 'Adds a cryptographic signature to outbound emails so recipients can verify they were sent by you and have not been tampered with in transit.' },
  caa:    { name: 'CAA',    full: 'Certificate Authority Auth.',  desc: 'Restricts which certificate authorities are permitted to issue SSL/TLS certificates for your domain, preventing unauthorised certificates being issued.' },
  dnssec: { name: 'DNSSEC', full: 'DNS Security Extensions',     desc: 'Digitally signs your DNS records to protect against DNS spoofing and cache poisoning attacks that could redirect your visitors to malicious sites.' },
};

export default function EmailSecurityDetail({
  slug: slugProp,
  hideHeader = false,
  // Ack props — provided by CyberSecurityHub, default to no-op
  acks     = {},
  isAdmin  = false,
  onAck    = null,
  onUnack  = null,
  onSummary = null,
}) {
  const { slug: urlSlug } = useParams();
  const { user }          = useAuth();
  const slug              = slugProp || urlSlug || user?.client_slug || '';

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!slug) return;
    authFetch(`/api/clients/${slug}/dns`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load'); setLoading(false); });
  }, [slug]);

  const dns     = data?.data;
  const results = dns?.results || {};
  const domains = Object.values(results);
  const total   = dns?.total_issues ?? domains.reduce((s, d) => s + domainIssueCount(d), 0);
  const color   = overallColor(total);
  const scanned = dns?.scanned_at
    ? new Date(dns.scanned_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'
    : null;

  // Report summary to analysis strip when loading settles
  useEffect(() => {
    if (!loading && !error && onSummary) {
      if (!data?.available) {
        onSummary({ status: 'nodata', count: 0 });
      } else {
        onSummary({ status: total === 0 ? 'clean' : 'issues', count: total });
      }
    }
  }, [loading, error, data, total, onSummary]);

  return (
    <div>
      {!hideHeader && (
        <div className="page-header">
          <div className="page-title">Email and Domain Security</div>
          <div className="page-subtitle">DNS security configuration across your domains</div>
        </div>
      )}
      {scanned && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>Last updated {scanned}</div>}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>
      ) : error ? (
        <div className="error-msg">{error}</div>
      ) : !data?.available ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
          No DNS scan data yet. Scans run automatically via Clearview Edge.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Issues found',        value: total,          color },
              { label: 'Domains',             value: domains.length, color: 'var(--accent)' },
              { label: 'Protections active',  value: domains.reduce((s, d) => s + CHECKS.filter(k =>  checkValid(d, k)).length, 0), color: '#30d158' },
              { label: 'Needs attention',     value: domains.reduce((s, d) => s + CHECKS.filter(k => !checkValid(d, k)).length, 0), color: total > 0 ? '#ff2d55' : 'var(--text-muted)' },
            ].map(({ label, value, color: c }) => (
              <div key={label} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', padding: '14px 20px', minWidth: 100,
              }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: c, fontFamily: 'var(--font-mono)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Per-domain cards */}
          {domains.map(domain => {
            const issues = domainIssueCount(domain);
            const dc     = overallColor(issues);
            return (
              <div key={domain.domain} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderLeft: `3px solid ${dc}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              }}>
                {/* Domain header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                  padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--card-bg)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                      {domain.domain}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: `${dc}18`, border: `1px solid ${dc}40`, color: dc,
                    }}>
                      {issues === 0 ? 'all checks passed' : `${issues} issue${issues !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                  {domain.mx?.records?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      MX: {domain.mx.records[0]}
                    </div>
                  )}
                </div>

                {/* Check rows */}
                {CHECKS.map((key, idx) => {
                  const valid  = checkValid(domain, key);
                  const issue  = checkIssue(domain, key);
                  const info   = CHECK_DESCRIPTIONS[key];
                  const rc     = valid ? '#30d158' : '#ff2d55';
                  const ackKey = `email::${key}::${domain.domain}`;

                  let recordValue = null;
                  if (key === 'spf'   && domain.spf?.records?.length)   recordValue = domain.spf.records[0].replace(/^"|"$/g, '');
                  if (key === 'dmarc' && domain.dmarc?.records?.length)  recordValue = domain.dmarc.records[0].replace(/^"|"$/g, '');
                  if (key === 'dkim'  && domain.dkim?.records?.length)   recordValue = `${domain.dkim.records.length} selector${domain.dkim.records.length !== 1 ? 's' : ''} found`;
                  if (key === 'caa'   && domain.caa?.records?.length)    recordValue = domain.caa.records.join(', ');

                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 16,
                      padding: '12px 20px',
                      borderBottom: idx < CHECKS.length - 1 ? '1px solid var(--border)' : 'none',
                      background: !valid ? `${rc}06` : 'transparent',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: 6, flexShrink: 0, marginTop: 1,
                        background: `${rc}18`, border: `1px solid ${rc}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 900, color: rc,
                      }}>
                        {valid ? '✓' : '✗'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{info.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{info.full}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4, marginBottom: (!valid && issue) || recordValue ? 6 : 0 }}>
                          {info.desc}
                        </div>
                        {!valid && issue && (
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#ff2d55', display: 'flex', alignItems: 'center', gap: 5, marginBottom: recordValue ? 4 : 0 }}>
                            <span>⚠</span> {issue}
                          </div>
                        )}
                        {recordValue && (
                          <div style={{
                            fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)',
                            background: 'var(--card-bg)', border: '1px solid var(--border)',
                            borderRadius: 4, padding: '4px 8px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {recordValue}
                          </div>
                        )}
                        {/* Acknowledgement — only shown on failed checks */}
                        {!valid && (
                          <InlineAck
                            checkKey={ackKey}
                            acks={acks}
                            isAdmin={isAdmin}
                            onAck={onAck}
                            onUnack={onUnack}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Why this matters */}
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '16px 20px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
              Why This Matters
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Email security records protect your domain from being used to send phishing emails impersonating your organisation.
              Without SPF, DMARC, and DKIM correctly configured, attackers can spoof your email address and send convincing
              fraudulent messages to your customers and partners. CAA and DNSSEC provide additional protection against
              certificate fraud and DNS-level attacks.
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
